import { useState, useEffect } from 'react';

// Singleton state manager for UFO segments
// This file should never be included in production code, do not add production code here
class UFODebugState {
	private static instance: UFODebugState;

	private state = false;

	private listeners: Set<(state: boolean) => void> = new Set();

	private globalMethodsSet = false;

	static getInstance(): UFODebugState {
		if (!UFODebugState.instance) {
			UFODebugState.instance = new UFODebugState();
		}
		return UFODebugState.instance;
	}

	getState(): boolean {
		return this.state;
	}

	setState(newState: boolean): void {
		if (this.state !== newState) {
			this.state = newState;
			this.notifyListeners();
		}
	}

	subscribe(listener: (state: boolean) => void): () => void {
		this.listeners.add(listener);

		// Set up global methods only once
		if (!this.globalMethodsSet) {
			this.setupGlobalMethods();
			this.globalMethodsSet = true;
		}

		// Return unsubscribe function
		return () => {
			this.listeners.delete(listener);
		};
	}

	private notifyListeners(): void {
		this.listeners.forEach((listener) => listener(this.state));
	}

	private setupGlobalMethods(): void {
		// @ts-expect-error This file should never be included in production code so we dont want to type globalThis as it shouldnt know about it
		globalThis.showUfoSegments = () => {
			this.setState(true);
			return 'Showing ufo segments';
		};

		// @ts-expect-error This file should never be included in production code so we dont want to type globalThis as it shouldnt know about it
		globalThis.hideUFOSegments = () => {
			this.setState(false);
			return 'Hiding ufo segments';
		};
	}
}

/**
 * Hook to use global UFO segment state
 * This ensures all components share the same state and global methods are only set once
 */
export function useShowUFOSegmentsState(): [boolean, (state: boolean) => void] {
	const manager = UFODebugState.getInstance();
	const [state, setState] = useState(manager.getState());

	useEffect(() => {
		const unsubscribe = manager.subscribe(setState);
		return () => {
			unsubscribe();
		};
	}, [manager]);

	return [state, setState];
}
